import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useStore, useTranslation } from '../store/useStore';
import {
  Search, ShoppingCart, User, Globe, Menu, X, ChevronDown,
  Shield, LogOut, Bell, Heart, Package, LayoutDashboard
} from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'Francais', flag: '🇫🇷' },
  { code: 'sw', name: 'Kiswahili', flag: '🇰🇪' },
  { code: 'es', name: 'Espanol', flag: '🇪🇸' },
  { code: 'pt', name: 'Portugues', flag: '🇧🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
];

export default function Header() {
  const { t, language } = useTranslation();
  const { user, cart, unreadCount, logout, setLanguage } = useStore();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdown, setLangDropdown] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/deals?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const mainNav = [
    { label: t('nav.deals'), href: '/deals' },
    { label: t('nav.categories'), href: '/categories' },
    { label: t('nav.suppliers'), href: '/supplier/register' },
    { label: t('nav.ipme'), href: '/ipme' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-20">
      {/* Liquid Glass Background */}
      <div className="liquid-glass absolute inset-0" />
      
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-4 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 bg-gradient-hero rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-[#0f172a] hidden sm:block font-display">IPO</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          {mainNav.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="px-3 py-2 text-sm font-medium text-[#1e293b] hover:text-[#c2410c] transition-colors rounded-lg hover:bg-white/30"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-1">
          {/* Search */}
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="p-2.5 rounded-lg hover:bg-white/30 transition-colors"
          >
            <Search className="w-5 h-5 text-[#1e293b]" />
          </button>

          {/* Language */}
          <div className="relative">
            <button
              onClick={() => setLangDropdown(!langDropdown)}
              className="hidden md:flex items-center gap-1 px-2 py-2 rounded-lg hover:bg-white/30 transition-colors"
            >
              <Globe className="w-5 h-5 text-[#1e293b]" />
              <span className="text-xs font-semibold uppercase text-[#1e293b]">{language}</span>
              <ChevronDown className="w-3 h-3 text-[#64748b]" />
            </button>
            {langDropdown && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden py-1 z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { setLanguage(lang.code); setLangDropdown(false); }}
                    className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-gray-50 transition-colors ${language === lang.code ? 'text-[#c2410c] font-medium bg-orange-50/50' : 'text-[#1e293b]'}`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                    {language === lang.code && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#c2410c]" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Cart */}
          <Link to="/cart" className="relative p-2.5 rounded-lg hover:bg-white/30 transition-colors">
            <ShoppingCart className="w-5 h-5 text-[#1e293b]" />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#c2410c] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Link>

          {/* Notifications */}
          {user && (
            <button className="relative p-2.5 rounded-lg hover:bg-white/30 transition-colors hidden sm:block">
              <Bell className="w-5 h-5 text-[#1e293b]" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#c2410c] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          )}

          {/* User Menu */}
          {user ? (
            <div className="relative">
              <button
                onClick={() => setUserDropdown(!userDropdown)}
                className="flex items-center gap-2 pl-2 pr-1 py-1.5 rounded-lg hover:bg-white/30 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#312e81] to-[#c2410c] flex items-center justify-center text-white text-sm font-semibold">
                  {user.firstName[0]}{user.lastName[0]}
                </div>
                <ChevronDown className="w-3 h-3 text-[#64748b] hidden sm:block" />
              </button>
              {userDropdown && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden py-1 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-[#1e293b]">{user.firstName} {user.lastName}</p>
                    <p className="text-xs text-[#64748b]">{user.email}</p>
                  </div>
                  <Link to="/dashboard" onClick={() => setUserDropdown(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#1e293b] hover:bg-gray-50">
                    <LayoutDashboard className="w-4 h-4" /> {t('nav.dashboard')}
                  </Link>
                  <Link to="/orders" onClick={() => setUserDropdown(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#1e293b] hover:bg-gray-50">
                    <Package className="w-4 h-4" /> {t('dashboard.orders')}
                  </Link>
                  <Link to="/wishlist" onClick={() => setUserDropdown(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#1e293b] hover:bg-gray-50">
                    <Heart className="w-4 h-4" /> {t('dashboard.wishlist')}
                  </Link>
                  {user.role === 'admin' || user.role === 'super_admin' ? (
                    <Link to="/admin" onClick={() => setUserDropdown(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#1e293b] hover:bg-gray-50">
                      <Shield className="w-4 h-4" /> {t('nav.admin')}
                    </Link>
                  ) : null}
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button onClick={() => { logout(); setUserDropdown(false); navigate('/'); }} className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full">
                      <LogOut className="w-4 h-4" /> {t('auth.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className="hidden sm:block px-4 py-2 text-sm font-medium text-[#1e293b] hover:text-[#c2410c] transition-colors"
              >
                {t('auth.login')}
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 text-sm font-medium bg-[#c2410c] text-white rounded-lg hover:bg-[#9a3412] transition-colors shadow-sm"
              >
                {t('auth.register')}
              </Link>
            </div>
          )}

          {/* Mobile Menu */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2.5 rounded-lg hover:bg-white/30 transition-colors"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Search Overlay */}
      {searchOpen && (
        <div className="absolute top-full left-0 right-0 bg-white shadow-xl border-t border-gray-100 p-4 z-40">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto flex gap-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search.placeholder')}
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
              autoFocus
            />
            <button type="submit" className="px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-medium">
              <Search className="w-5 h-5" />
            </button>
          </form>
        </div>
      )}

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t border-gray-100 py-4 z-40">
          <nav className="flex flex-col px-4">
            {mainNav.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-medium text-[#1e293b] hover:text-[#c2410c] hover:bg-gray-50 rounded-lg transition-colors"
              >
                {item.label}
              </Link>
            ))}
            {!user && (
              <>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-sm font-medium text-[#1e293b] hover:bg-gray-50 rounded-lg">
                  {t('auth.login')}
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="px-4 py-3 text-sm font-medium bg-[#c2410c] text-white rounded-lg text-center mt-2">
                  {t('auth.register')}
                </Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
